/**
 * The deterministic auditor engine (04-llm-role.md §5). `runAudit` folds a set
 * of campaign entities through the registered rules, stamps each advisory with
 * its content-addressed id, applies per-Campaign severity overrides (§5.2.1),
 * de-duplicates by id, and returns a deterministically-ordered list.
 *
 * The engine is the FLOOR: pure, synchronous, dependency-light. The LLM
 * augmentation (§5.4) is a separate additive layer that never gates the
 * deterministic rules.
 */
import type { Calendar } from '@dl/calendar';
import type {
  ActiveSpoofBelief,
  BundleRegistry,
  CausalPredicateSpec,
  EntityState,
  FacilitationMode,
  PredicateRegistry,
  RevealTarget,
  VisibilityMode,
} from '@dl/shared';

import {
  compareAdvisories,
  stampAdvisory,
  type Advisory,
  type RawAdvisory,
  type Severity,
  type SeverityOverride,
} from './advisory.js';
import { ENTITY_STATE_RULES, type EntityStateRule } from './rules/index.js';


/**
 * Everything a rule needs to evaluate. Entities are the folded current-value
 * projection (the op log stays authoritative; this is a rebuildable view). The
 * registry resolves predicate declarations (value kinds, ref roots,
 * cardinality, merge). Overrides are the Campaign's `auditor_rule_overrides`.
 */
export interface AuditContext {
  entities: Record<string, EntityState>;
  registry: PredicateRegistry;
  /** Per-rule severity overrides (§5.2.1). Absent → engine defaults. */
  overrides?: Record<string, SeverityOverride>;
  /**
   * The opaque predicate ids that mark a subject as GONE — death / destruction
   * (ADR-0005 §"Auditor"; #197). Feeds the `temporal.liveness` cross-fact rule
   * ("a `died_in` fact but a still-open segment"). Predicate identity is an
   * opaque id (ADR-0004 §1) with no semantic flag, so the terminal set is
   * supplied by the caller (the client passes the official-core `died_in` id +
   * any homebrew terminals a GM marks). Absent / empty ⇒ the liveness rule is a
   * no-op (progressive disclosure).
   */
  liveness_terminal_predicates?: readonly string[];
  /**
   * The directed causal/precedence predicates whose composed "happens-before"
   * graph must stay acyclic (`06-timeline.md` §8; #222). Each entry pairs an
   * opaque predicate id with the direction its edge points (`caused_by` →
   * object-before-subject, `precedes` → subject-before-object). Feeds the
   * `causal.cycle` rule. As with {@link liveness_terminal_predicates}, identity
   * is an opaque id with no semantic flag, so the caller supplies the set (the
   * client passes {@link OFFICIAL_CORE_CAUSAL_PREDICATES}). Absent / empty ⇒
   * the cycle rule is a no-op (progressive disclosure).
   */
  causal_predicates?: readonly CausalPredicateSpec[];
  /**
   * The `recurs_as` recurrence binding for the `recurrence.open_cycle` rule
   * (`06-timeline.md` §10.4; #239). `recurs_as_predicates` are the opaque
   * predicate ids whose object is a `RecurrenceRule` (the client passes the
   * official-core `recurs_as` id); `calendars` are the active/declared
   * calendars to resolve cycle names against (a cycle is "open" if any of
   * them declares it open). As with the other bindings, identity is an opaque
   * id (ADR-0004 §1) with no semantic flag, so the caller supplies the set.
   * Absent / empty ⇒ the rule is a no-op (progressive disclosure).
   */
  recurrence_open_cycle?: {
    recurs_as_predicates: readonly string[];
    calendars: readonly Calendar[];
  };
  /**
   * Context for the cursor-visibility advisory rules (06-timeline.md §13.5).
   * Absent / undefined ⇒ the cursor-visibility rules are a no-op (progressive
   * disclosure — matching the `recurrence_open_cycle` pattern).
   *
   * - `visibility_map`          The folded per-RevealTarget visibility map
   *                             from `foldCursorVisibilityOps`.
   * - `has_active_session`      Whether a session is currently active.
   * - `browse_diverged_since_ops` How many ops have been written while the
   *                             browse cursor is diverged from the session
   *                             cursor without a copy-forward fact.
   */
  cursor_visibility_ops?: {
    visibility_map: Partial<Record<RevealTarget, VisibilityMode>>;
    has_active_session: boolean;
    browse_diverged_since_ops: number;
  };
  /**
   * The facilitation mode + membership shape for the `facilitation.mode_mismatch`
   * rule (`07-session-experience.md` §2.2; #256). `mode` is the campaign's chosen
   * facilitation mode; `memberCount` is its total member count; `hasGmRole` is
   * whether any member holds the `gm` Campaign role (the implicit-GM owner counts
   * as a GM-roled member). Absent ⇒ the rule is a no-op (progressive disclosure —
   * a campaign that has not surfaced its membership shape never fires it).
   */
  facilitation?: {
    mode: FacilitationMode;
    memberCount: number;
    hasGmRole: boolean;
  };
  /**
   * Context for the spoof-in-effect advisory rule (06-timeline.md §13.3/§13.5,
   * #254). Absent / undefined ⇒ the spoof rule is a no-op (progressive
   * disclosure — same pattern as `cursor_visibility_ops`).
   *
   * - `active_spoofs`               Resolved `ActiveSpoofBelief` records (one
   *                                 per Character with an open `cursor_spoof`
   *                                 belief). Empty ⇒ no advisory. Supplied by
   *                                 the caller from `collectActiveSpoofBeliefs`.
   * - `facts_authored_during_spoof` Count of fact-create/edit ops whose session
   *                                 cursor falls inside ANY active spoof window.
   *                                 The engine stays pure — the caller aggregates
   *                                 this count from the op log. Zero is valid.
   */
  cursor_spoof_ops?: {
    active_spoofs: readonly ActiveSpoofBelief[];
    facts_authored_during_spoof: number;
  };
  /**
   * The suggestion-set bundle registry (#251) — folded from the op log.
   * Feeds the `combat.quick_log_unreviewed` rule (#259). Absent ⇒ the
   * rule is a no-op (progressive disclosure).
   */
  bundles?: BundleRegistry;
  /**
   * Override for the current wall-clock time in milliseconds. Defaults to
   * `Date.now()` when absent. Injected in tests for deterministic clocks.
   */
  now_ms?: number;
}

/** A deterministic rule: pure `(context) → raw advisories`. */
export type Rule = EntityStateRule;

/**
 * Apply a Campaign severity override to one raw advisory. Returns `null` when
 * the rule is muted (`'off'`). A `blocking_error` is NEVER downgraded — a
 * structural-integrity rule still refuses the write regardless of GM
 * preference (§5.1: only `blocking_error` refuses; §5.2.1: overrides apply to
 * the dismissible tiers). This clamp keeps a GM from accidentally muting a
 * cycle-detection that the renderer depends on.
 */
function applyOverride(
  raw: RawAdvisory,
  override: SeverityOverride | undefined,
): RawAdvisory | null {
  if (raw.severity === 'blocking_error') return raw;
  if (override === undefined) return raw;
  if (override === 'off') return null;
  return { ...raw, severity: override };
}

/**
 * Run the deterministic auditor over a campaign snapshot.
 *
 * - Each rule contributes raw advisories.
 * - Severity overrides are applied (mute / downgrade / promote).
 * - Ids are content-addressed and de-duplicated (two rules that surface the
 *   same `(rule, facts, entities)` collapse to one advisory).
 * - The result is sorted deterministically (severity, rule, id).
 */
export function runAudit(
  ctx: AuditContext,
  rules: readonly Rule[] = ENTITY_STATE_RULES,
): Advisory[] {
  const overrides = ctx.overrides ?? {};
  const byId = new Map<string, Advisory>();

  for (const rule of rules) {
    for (const raw of rule(ctx)) {
      const adjusted = applyOverride(raw, overrides[raw.rule]);
      if (adjusted === null) continue;
      const advisory = stampAdvisory(adjusted);
      // Content-addressing makes duplicate detection a map insert; first
      // writer wins (rules are pure, so the value is identical anyway).
      if (!byId.has(advisory.id)) byId.set(advisory.id, advisory);
    }
  }

  return [...byId.values()].sort(compareAdvisories);
}

export type { Advisory, RawAdvisory, Severity, SeverityOverride };
